// Repeatable title-sheet facts and document reconciliation.
export const patientTitleSheet = {
  "patient.death.mergeMismatch": "The charts have different death dates. Reconcile them on the title sheets before merging.",
  "patient.death.label": "Patient deceased",
  "patient.death.date": "Date of death",
  "patient.death.edit": "Record or correct death",
  "patient.death.readOnly": "The patient is deceased. Adding or changing encounters, protocols and diagnoses is unavailable.",
  "patient.death.dateRequired": "Enter the date of death.",
  "patient.death.reason": "Reason for correction",
  "patient.death.reasonRequired": "Enter a reason for correcting the death record.",
  "patient.death.invalid": "Enter a valid date of death and a reason for correction.",
  "patient.death.unsaved": "Unsaved changes to the patient’s death record",
  "patient.death.age": "Age at death",

  "patient.facts.address.useManual": "Enter manually instead",
  "patient.facts.address.manualWarning": "Manual address — not suitable for automatic address exchange.",
  "patient.facts.address.registryMethod": "Selected from an address registry",

  "patient.facts.addresses": "Addresses",
  "patient.facts.edit.addresses": "Edit addresses",
  "patient.facts.history.addresses": "Address history",
  "patient.facts.address.text": "Full address as recorded",
  "patient.facts.address.components": "Address components",
  "patient.facts.address.componentsHint": "Optional. Fill the components that apply to this address; keep the full address above consistent with them.",
  "patient.facts.address.registryText": "Full address from registry",
  "patient.facts.address.gar": "Select from GAR",
  "patient.facts.address.garLoading": "Loading addresses…",
  "patient.facts.address.garUnavailable": "The address registry is unavailable. You can keep entering the address manually.",
  "patient.facts.address.garQuery": "City, street or address number",
  "patient.facts.address.garSearch": "Find address",
  "patient.facts.address.garReset": "Search all addresses",
  "patient.facts.address.garFailure": "The address could not be loaded. Your address draft is preserved; try the search again.",
  "patient.facts.address.garEmpty": "No addresses found.",
  "patient.facts.address.garChildren": "Choose a street, house or apartment within this address",
  "patient.facts.address.garSelect": "Use this address",
  "patient.facts.address.garStale": "This registry selection is no longer available. Select the address again; your draft is preserved.",
  "patient.facts.address.region": "Region / state / province",
  "patient.facts.address.district": "District / area",
  "patient.facts.address.locality": "City / locality",
  "patient.facts.address.localityType": "Locality type",
  "patient.facts.address.street": "Street",
  "patient.facts.address.house": "House number / name",
  "patient.facts.address.building": "Building / block",
  "patient.facts.address.unit": "Apartment / unit",
  "patient.facts.address.postalCode": "Postal code",
  "patient.facts.address.purpose.unspecified": "Unspecified",
  "patient.facts.address.purpose.home": "Home / actual residence",
  "patient.facts.address.purpose.registration": "Registered address",
  "patient.facts.address.purpose.temporary": "Temporary residence",
  "patient.facts.address.purpose.postal": "Postal address",
  "patient.facts.address.purpose.work": "Work address",
  "patient.facts.address.purpose.other": "Other",
  "patient.facts.address.state.current": "Current",
  "patient.facts.address.state.former": "Former",
  "patient.facts.address.state.unspecified": "Unspecified",

  "patient.identity.countryOption": "{action} {state} ({start}–{end})",
  "patient.identity.sourceChanged": "The files changed after recognition. Recognize them again before saving; your draft is preserved.",
  "patient.identity.addCitizenship": "Add citizenship",
  "patient.identity.addResidence": "Add country of residence",
  "patient.identity.addName": "Add name variant",
  "patient.identity.printedNames": "Names printed in the document",
  "patient.identity.permitCountry": "Country granting residence permission",
  "patient.identity.permitHint": "A permit does not establish usual residence. Confirm residence and its period separately.",
  "patient.identity.compare": "Compare with the patient record",
  "patient.identity.selectionHint": "Select only the changes to apply. Saving the document alone leaves patient details unchanged.",
  "patient.identity.same": "Already recorded",
  "patient.identity.countryAction": "Add or link a country record",
  "patient.identity.newPeriod": "Add a new period",
  "patient.identity.add": "Add record",
  "patient.identity.link": "Link and refine existing record",
  "patient.identity.nameAction": "Add or correct a name",
  "patient.identity.formerNameEnd": "For an actual name change: last date the old name was used. Leave empty for a correction.",
  "patient.identity.nameAmbiguous": "Review the name variants and choose the preferred spelling before applying.",
  "patient.identity.resultingName": "Resulting displayed name",
  "patient.identity.acceptName": "I accept this displayed name",
  "patient.identity.applyRotation": "Apply Google orientation",
  "patient.identity.conflict": "The document or patient details changed. Your draft is preserved; reload the saved document or compare patient details again.",
  "patient.identity.recompare": "Read current patient details and compare again",
  "patient.identity.recompared": "Current data loaded. Review and select the changes again.",
  "patient.identity.countryExists": "This country already has an ongoing record. Select that record to link or refine it.",
  "patient.identity.primaryExists": "Another policy is primary. Explicitly confirm its reassignment.",
  "patient.identity.replaceDraft": "Recognize again and replace draft fields?",
  "patient.identity.replaceDraftDetail": "Recognition replaces the document fields you have entered. Patient details remain unchanged until you select and apply changes.",
  "patient.identity.insuranceRole": "Policy role",
  "patient.identity.role.primary": "Primary",
  "patient.identity.role.secondary": "Secondary",
  "patient.identity.reassignPrimary": "Make this existing primary policy secondary:",
  "patient.identity.insurerPhone": "Insurer phone",
  "patient.identity.insurerEmail": "Insurer email",
  "patient.identity.assistanceName": "Assistance service",
  "patient.identity.assistancePhone": "Assistance phone",
  "patient.identity.assistanceEmail": "Assistance email",
  "patient.identity.errorReason": "Reason for marking the record erroneous",
  "patient.identity.requisites": "Document requisites",
  "patient.identity.policyRequisites": "Policy requisites",
  "patient.identity.moreRequisites": "More",
  "patient.identity.moreRequisitesTitle": "Issue date and issuing authority",
  "patient.identity.documentData": "Data in the document",
  "patient.identity.furtherCitizenship": "Further citizenship",
  "patient.identity.permitBrief": "Residence permit",
  "patient.identity.refine": "Refine",
  "patient.identity.countriesTitle": "Citizenship, residence and permit",
  "patient.identity.done": "Done",
  "patient.identity.spellings": "Spellings: {count}",
  "patient.identity.languageUnknown": "Language not set",
  "patient.identity.international": "international",
  "patient.identity.editName": "Edit the name {name}",
  "patient.identity.nameInDocument": "Name in the document",
  "patient.identity.sourceDocument": "Source document",
  "patient.identity.sourceFiles": "Files: {count}",
  "patient.identity.enlarge": "Enlarge",
  "patient.identity.windowTabs": "Document window sections",
  "patient.identity.tabFields": "Requisites and data",
  "patient.identity.tabChanges": "Changes to the patient record",
  "patient.identity.pickedOf": "{selected}/{total}",
  "patient.identity.currentChart": "Current patient record",
  "patient.identity.transferOf": "How to transfer: {name}",
  "patient.identity.documentOnly": "Document only",
  "patient.identity.saveAndApply": "Save and apply · {count}",
  "patient.identity.pickedCount": "Changes selected: {count}",
  "patient.identity.chartUnchanged": "The patient record stays unchanged",
  "patient.identity.checkBeforeSaving": "Check the values against the scan before saving.",
  "patient.identity.erroneousHolds": "A record marked erroneous does not change the patient record. Save the document only.",
  "patient.identity.acceptHolds": "Accept the resulting displayed name on the changes tab.",
  "patient.facts.country": "Country",
  "patient.facts.state": "State",
  "patient.facts.state.current": "Current",
  "patient.facts.state.former": "Former",
  "patient.facts.state.unspecified": "Not specified",
  "patient.facts.details": "Details",
  "patient.facts.start": "From",
  "patient.facts.end": "Until",
  "patient.facts.partialDate": "YYYY, YYYY-MM or YYYY-MM-DD",
  "patient.facts.source": "Source",
  "patient.facts.source.reported": "As reported",
  "patient.facts.source.document": "Document",
  "patient.facts.source.unknown": "Not recorded",
  "patient.facts.document": "Source document",
  "patient.facts.note": "Note",
  "patient.facts.erroneous": "Entered in error",
  "patient.facts.citizenships": "Citizenships",
  "patient.facts.residences": "Residences",
  "patient.facts.contacts": "Contact details",
  "patient.facts.removeDraft": "Remove draft entry",
  "patient.facts.add": "Add entry",
  "patient.facts.deliveryInUse": "Disconnect the linked delivery channel before changing or removing this contact.",
  "patient.facts.stale": "These details changed in another session. Reload the saved values before editing again.",
  "patient.facts.contactType": "Contact type",
  "patient.facts.contactType.phone": "Phone",
  "patient.facts.contactType.email": "Email",
  "patient.facts.purpose": "Purpose",
  "patient.facts.purpose.unspecified": "Not specified",
  "patient.facts.purpose.personal": "Personal",
  "patient.facts.purpose.work": "Work",
  "patient.facts.purpose.travel": "Travel",
  "patient.facts.purpose.other": "Other",
  "patient.facts.preferred": "Preferred",
  "patient.facts.verifiedContact": "Contact confirmed",
  "patient.facts.count": "Records: {count}",
  "patient.facts.period": "Period",
  "patient.facts.former": "Former records",
  "patient.facts.older": "Earlier revisions",
  "patient.facts.hoursNotEnforced": "Delivery hours are saved, but patient messages are not yet restricted to this time window.",
  "patient.facts.edit.citizenships": "Edit citizenships",
  "patient.facts.edit.residences": "Edit residences",
  "patient.facts.edit.contacts": "Edit contact details",
  "patient.facts.history.citizenships": "History: citizenships",
  "patient.facts.history.residences": "History: residences",
  "patient.facts.history.contacts": "History: contact details",
  "patient.facts.channels": "Messaging channels",
  "patient.documents.downloadScanNamed": "Download scan: {name}",
  "patient.documents.editNamed": "Edit {name}",
  "patient.documents.expiredOn": "Expired {date}",
  "patient.sheet.menu": "Title sheet: view and actions",
  "patient.sheet.layout": "Sheet layout",
  "patient.sheet.layout.dossier": "Dossier",
  "patient.sheet.layout.passport": "Passport and stream",
  "patient.sheet.chart": "Chart",
  "patient.sheet.actsOf": "{part}: actions",
  "patient.sheet.actIn": "{act}: {part}",
  "patient.sheet.add": "Add",
  "patient.sheet.facts": "Key facts",
  "patient.sheet.fact.age": "Age",
  "patient.sheet.fact.citizenship": "Citizenship",
  "patient.sheet.fact.residence": "Lives in",
  "patient.sheet.fact.consent": "Consent",
  "patient.sheet.basic": "Basic data",
  "patient.sheet.keeping": "Chart keeping",
  "patient.sheet.contact": "Contact",
  "patient.sheet.marks": "Marks",
  "patient.sheet.edit.basic": "Edit basic data",
  "patient.sheet.edit.registration": "Edit registration",
  "patient.sheet.messengers.manage": "Manage messaging channels",
  "patient.sheet.consent.preview": "Preview form",
  "patient.sheet.consent.blank": "Download blank",
  "patient.sheet.consent.signed": "Download signed",
  "patient.sheet.consent.revoke": "Revoke",
  "patient.documents.noExpiry": "no expiry",
  "patient.documents.until": "until {date}",
  "patient.documents.hideExpired": "Hide expired",
  "patient.documents.showExpiredCount": "Show expired ({count})",
  "patient.sheet.docs.document": "Document",
  "patient.sheet.docs.number": "Number",
  "patient.sheet.docs.country": "Country",
  "patient.sheet.docs.term": "Validity",
  "patient.sheet.docs.role": "Role",
  "patient.sheet.docs.kind": "Type",
  "patient.facts.periodBetween": "{start} – {end}",
  "patient.facts.periodSince": "since {start}",
  "patient.facts.periodUntil": "until {end}",
  "patient.facts.formerly": "formerly:",
  "patient.sheet.channels": "Communication channels",
  "patient.sheet.channels.channel": "Channel",
  "patient.sheet.channels.address": "Address",
  "patient.sheet.channels.messages": "Clinic messages",
  "patient.sheet.channels.allowed": "allowed",
  "patient.sheet.channels.notChosen": "not chosen",
  "patient.sheet.channels.blocked": "blocked",
  "patient.sheet.channels.noMessages": "No message types chosen",
  "patient.sheet.channels.links": "Messengers",
  "patient.sheet.channels.settings": "Preferences",
  "patient.sheet.consentAccepted": "Consent accepted",
  "patient.sheet.consentMissing": "No consent",
} as const;
